/* osyn-games-marquee.js — Marquee jeux (cartes bakées) -----------------------
 * Moteur de SECTION (auto-portant) — n'utilise PAS le moteur slider global.
 *  - Auto-scroll = transform: translate3d (compositor-smooth, pas de jitter scrollLeft).
 *  - Vélocité UNIFIÉE par rangée : v += (target − v)·K → gère drift easé, rampe
 *    start/stop ET handoff fling→drift d'un seul modèle (jamais zéro-puis-snap).
 *  - Drag souris + tactile (même chemin pointeur), 1:1, inertie au relâché.
 *  - Pause : hover, drag, onglet caché (document.hidden lu chaque frame), hors-écran
 *    (IntersectionObserver), focus clavier. reduced-motion / grille / auto-scroll off
 *    → pas de moteur (scroll natif géré en CSS).
 *  - Boucle sans couture : clone du set 1× ; wrap x += setW (setW mesuré au DOM via
 *    offsetLeft → indépendant du transform, gère les largeurs variables).
 * -------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.customElements && customElements.get('osyn-games-marquee')) return;

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MOBILE = window.matchMedia('(max-width: 768px)');
  var K = 0.06;        // lissage vélocité (~friction iOS 0.94/frame)
  var NUDGE = 140;     // px par flèche clavier

  class OsynGamesMarquee extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.layout = this.dataset.layout || 'marquee';
      this.autoscroll = this.dataset.autoscroll === 'true';
      this.spD = parseFloat(this.dataset.speed) || 20;
      this.spM = parseFloat(this.dataset.speedMobile) || 14;

      // moteur transform actif uniquement en marquee animé
      this._animate = this.layout === 'marquee' && this.autoscroll && !REDUCE.matches;
      if (!this._animate) {
        // marquee + auto-scroll OFF → scroll natif (CSS .osyn-gm--static) ; grille/reduce = CSS
        if (this.layout === 'marquee' && !this.autoscroll && !REDUCE.matches) {
          this.classList.add('osyn-gm--static');
        }
        return;
      }

      // démarrage différé : rien (clones, mesures, boucle d'animation) avant que la section
      // n'approche de l'écran, puis au premier moment libre du navigateur
      if ('IntersectionObserver' in window) {
        this._bootIO = new IntersectionObserver(function (ents) {
          if (!ents[0].isIntersecting) return;
          this._bootIO.disconnect(); this._bootIO = null;
          var boot = this._boot.bind(this);
          if ('requestIdleCallback' in window) requestIdleCallback(boot, { timeout: 500 }); else setTimeout(boot, 0);
        }.bind(this), { rootMargin: '300px 0px' });
        this._bootIO.observe(this);
      } else {
        this._boot();
      }
    }

    _boot() {
      if (this._booted || !this.isConnected) return;
      this._booted = true;
      this.tracks = [].slice.call(this.querySelectorAll('.osyn-gm__row')).map(function (row) {
        var track = row.querySelector('[data-osyn-gm-track]');
        return track && {
          row: row, track: track,
          dir: row.getAttribute('data-osyn-gm-row') === 'right' ? 1 : -1,
          x: 0, v: 0, setW: 0, realCount: track.children.length,
          dragging: false, moved: false
        };
      }).filter(Boolean);
      if (!this.tracks.length) return;

      this._hover = false; this._focus = false; this._down = false; this._visible = true; this._last = 0;

      this._buildClones();
      this._measure();
      this._bindDrag();

      this._onResize = this._scheduleResize.bind(this);
      window.addEventListener('resize', this._onResize);
      this._onLoad = this._scheduleResize.bind(this);
      window.addEventListener('load', this._onLoad);

      // hover-pause scopé à la ZONE DES CARTES (viewport), pas à l'hôte → reprend dès
      // qu'on quitte les images (et non toute la section). _hoverTarget = propriété
      // (retrait symétrique dans disconnectedCallback, avec garde).
      this._onEnter = function () { this._hover = true; }.bind(this);
      this._onLeave = function () { this._hover = false; }.bind(this);
      this._hoverTarget = this.querySelector('.osyn-gm__viewport') || this;
      this._hoverTarget.addEventListener('pointerenter', this._onEnter);
      this._hoverTarget.addEventListener('pointerleave', this._onLeave);

      // pause au focus clavier (a11y, remplace le bouton pause retiré)
      this._onFocusIn = function () { this._focus = true; }.bind(this);
      this._onFocusOut = function () { this._focus = false; }.bind(this);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);

      this._tickB = this._tick.bind(this);
      if ('IntersectionObserver' in window) {
        // hors écran : boucle d'animation coupée (aucune frame dépensée), relancée au retour
        this._io = new IntersectionObserver(function (ents) {
          this._visible = ents[0].isIntersecting;
          if (this._visible && !this._raf) { this._last = 0; this._raf = requestAnimationFrame(this._tickB); }
          else if (!this._visible && this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
        }.bind(this), { threshold: 0.01 });
        this._io.observe(this);
      }
      this._raf = requestAnimationFrame(this._tickB);
    }

    disconnectedCallback() {
      if (this._bootIO) this._bootIO.disconnect();
      if (this._raf) cancelAnimationFrame(this._raf);
      clearTimeout(this._rzT);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onLoad);
      if (this._hoverTarget) {                       // garde : les listeners hover ne sont posés que si _animate (sinon undefined)
        this._hoverTarget.removeEventListener('pointerenter', this._onEnter);
        this._hoverTarget.removeEventListener('pointerleave', this._onLeave);
      }
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      if (this._io) this._io.disconnect();
    }

    _targetSpeed() { return MOBILE.matches ? this.spM : this.spD; }

    _buildClones() {
      this.tracks.forEach(function (t) {
        if (t.track.dataset.gmCloned) { t.realCount = t.track.children.length / 2 | 0; return; }
        t.track.dataset.gmCloned = '1';
        var kids = [].slice.call(t.track.children);
        kids.forEach(function (c) {
          var cl = c.cloneNode(true);
          cl.setAttribute('aria-hidden', 'true');
          cl.tabIndex = -1;
          var a = cl.querySelector('a'); if (a) a.tabIndex = -1;
          t.track.appendChild(cl);
        });
      });
    }

    _measure() {
      this.tracks.forEach(function (t) {
        var first = t.track.children[0];
        var firstClone = t.track.children[t.realCount];
        // offsetLeft = indépendant du transform → setW fiable même piste translatée
        t.setW = (first && firstClone) ? (firstClone.offsetLeft - first.offsetLeft) : t.track.scrollWidth / 2;
      });
    }

    _applyWrap(t) {
      if (!t.setW) return;
      while (t.x <= -t.setW) t.x += t.setW;
      while (t.x > 0) t.x -= t.setW;
    }
    _render(t) { t.track.style.transform = 'translate3d(' + t.x.toFixed(2) + 'px,0,0)'; }

    _bindDrag() {
      var self = this;
      this.tracks.forEach(function (t) {
        t.track.addEventListener('dragstart', function (e) { e.preventDefault(); });

        t.track.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          e.preventDefault();
          t.dragging = true; t.moved = false;
          t.startX = e.clientX; t.startTX = t.x; t.lastX = e.clientX; t.lastT = e.timeStamp; t.vRaw = 0;
          self._down = true;
          try { t.track.setPointerCapture(e.pointerId); } catch (err) {}
          t.track.classList.add('is-grabbing', 'is-moving');
        });

        t.track.addEventListener('pointermove', function (e) {
          if (!t.dragging) return;
          var dx = e.clientX - t.startX;
          if (Math.abs(dx) > 4) t.moved = true;
          t.x = t.startTX + dx;
          var dt = e.timeStamp - t.lastT;
          if (dt > 0) { t.vRaw = 0.8 * ((e.clientX - t.lastX) / dt) + 0.2 * t.vRaw; t.lastX = e.clientX; t.lastT = e.timeStamp; }
          self._applyWrap(t);
          self._render(t);
        });

        var end = function () {
          if (!t.dragging) return;
          t.dragging = false;
          t.v = (t.vRaw || 0) * 1000;             // px/ms → px/s : la vélocité du fling alimente v (handoff continu)
          t.track.classList.remove('is-grabbing');
          self._down = self.tracks.some(function (o) { return o.dragging; });
        };
        t.track.addEventListener('pointerup', end);
        t.track.addEventListener('pointercancel', end);
        // clic post-drag → ne pas activer le lien
        t.track.addEventListener('click', function (e) { if (t.moved) { e.preventDefault(); e.stopPropagation(); } }, true);

        // clavier
        t.track.tabIndex = 0;
        t.track.setAttribute('role', 'group');
        var heading = self.querySelector('h2');
        if (heading) {
          if (!heading.id) heading.id = 'osyn-gm-h-' + Math.random().toString(36).slice(2, 9);
          t.track.setAttribute('aria-labelledby', heading.id);
        }
        t.track.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { e.preventDefault(); t.x -= NUDGE; self._applyWrap(t); self._render(t); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); t.x += NUDGE; self._applyWrap(t); self._render(t); }
        });
      });
    }

    _moving() {
      return !this._hover && !this._focus && !this._down && this._visible && !document.hidden;
    }

    _tick(ts) {
      this._raf = requestAnimationFrame(this._tickB);
      var dt = this._last ? Math.min(50, ts - this._last) : 16;   // ms, clamp anti-saut (onglet, lag)
      this._last = ts;
      var go = this._moving();
      var sp = this._targetSpeed();
      var self = this;
      this.tracks.forEach(function (t) {
        if (t.dragging) return;                       // x piloté par le drag
        var target = go ? sp * t.dir : 0;             // vitesse cible signée (px/s)
        t.v += (target - t.v) * K;                    // UN modèle : drift easé + rampe start/stop + handoff fling
        if (Math.abs(t.v) < 0.05 && target === 0) t.v = 0;
        if (t.v) { t.x += (t.v * dt) / 1000; self._applyWrap(t); self._render(t); }
        t.track.classList.toggle('is-moving', Math.abs(t.v) > 0.5);
      });
    }

    _scheduleResize() { clearTimeout(this._rzT); this._rzT = setTimeout(this._doResize.bind(this), 150); }
    _doResize() { this._measure(); this.tracks.forEach(function (t) { this._applyWrap(t); this._render(t); }.bind(this)); }
  }

  if (window.customElements) customElements.define('osyn-games-marquee', OsynGamesMarquee);
})();
